import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService, PlatformStatsDailyPoint, PlatformStatsOverviewResponse } from '../../shared/services/api.service';

type MetricCard = {
  label: string;
  value: number | string;
  hint?: string;
  tooltip?: string;
};

@Component({
  selector: 'app-platform-stats',
  templateUrl: './platform-stats.component.html',
  styleUrls: ['./platform-stats.component.css'],
})
export class PlatformStatsComponent implements OnInit {
  loading = true;
  stats: PlatformStatsOverviewResponse | null = null;

  private get monthStartHint(): string {
    const monthName = new Intl.DateTimeFormat('en-ZA', {
      timeZone: 'Africa/Johannesburg',
      month: 'long',
    }).format(new Date());
    return `from 1 ${monthName}`;
  }

  constructor(
    private readonly apiService: ApiService,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.refresh();
  }

  refresh(): void {
    this.loading = true;
    this.apiService.getPlatformStatsOverview().subscribe({
      next: (response) => {
        this.stats = response;
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        const message = error?.error?.error?.message || 'Failed to load platform stats';
        this.snackBar.open(message, 'Close', { duration: 5000 });
      },
    });
  }

  get metricCards(): MetricCard[] {
    const summary = this.stats?.summary;
    if (!summary) {
      return [];
    }
    const monthStartHint = this.monthStartHint;
    return [
      { label: 'Lookups Today', value: summary.lookupsToday },
      { label: 'Lookups This Week', value: summary.lookupsThisWeek },
      {
        label: 'Lookups This Month',
        value: summary.lookupsThisMonth,
        hint: monthStartHint,
        tooltip: 'Tenant requests created this month. This is demand volume from the tenant side.',
      },
      {
        label: 'Served From Core DB (Month)',
        value: summary.servedFromCoreDbThisMonth,
        hint: monthStartHint,
        tooltip: 'Core lookup calls served from cached/core-local data instead of a live provider call.',
      },
      {
        label: 'Served From XDS (Month)',
        value: summary.servedFromXdsThisMonth,
        hint: monthStartHint,
        tooltip: 'Core lookup calls that were not cache-served and were routed to XDS.',
      },
      { label: 'Completed This Month', value: summary.completedThisMonth, hint: monthStartHint },
      { label: 'Failed This Month', value: summary.failedThisMonth, hint: monthStartHint },
      { label: 'Pending Right Now', value: summary.pendingNow },
      { label: 'Success Rate (30d)', value: `${summary.successRate30d.toFixed(1)}%` },
      { label: 'Active Tenants This Week', value: summary.activeTenantsThisWeek },
      { label: 'Active Customers This Week', value: summary.activeCustomersThisWeek },
      {
        label: 'Credits Consumed This Month',
        value: summary.creditsConsumedThisMonth,
        hint: monthStartHint,
        tooltip: 'Sum of applied positive charge receipts this month. Not every lookup necessarily consumes credits immediately.',
      },
    ];
  }

  get lookupsDaily30d(): PlatformStatsDailyPoint[] {
    return this.stats?.trends?.lookupsDaily30d || [];
  }

  get lookupsDailyMax(): number {
    return Math.max(1, ...this.lookupsDaily30d.map((point) => Number(point.lookups || 0)));
  }

  getBarHeight(value: number, max: number): string {
    const safeMax = Math.max(1, max);
    const normalized = Math.max(0, Number(value || 0));
    const pct = Math.max(6, Math.round((normalized / safeMax) * 100));
    return `${pct}%`;
  }

  formatDayLabel(day: string): string {
    const parsed = new Date(day);
    if (Number.isNaN(parsed.getTime())) {
      return day;
    }
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const date = String(parsed.getDate()).padStart(2, '0');
    return `${month}/${date}`;
  }

  trackByDay(_index: number, point: PlatformStatsDailyPoint): string {
    return point.day;
  }
}
